import { useReducer, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation, Outlet, Navigate } from 'react-router-dom';
import { userAppContext, searchContext } from '../context/Context';

// Migration utilities
import { useContextStoreSync, useStateSource } from '../store/migration/useContextStoreSync';
import { useAuth, useNavigation } from '../store/index';

// Components
import Home from '../pages/Home';
import Support from '../pages/Support';
import Privacy from '../pages/PrivacyPolicy';
import CookiesPolicy from '../pages/CookiesPolicy';
import Api from '../pages/Api';
import ProfileArea from '../pages/ProfileArea';
import NotFoundContent from '../pages/NotFound';
import BadParamsContent from '../pages/BadParams';
import About from '../pages/About';
import LegalNotices from '../pages/LegalNotices';
import LegalNotice from '../pages/LegalNotice';
import AccountActivation from '../pages/AccountActivation';
import AccountActivationError from '../pages/AccountActivationError';
import Reset from '../pages/Reset';
import Search from '../modules/search/Search';
import LoginPage from '../modules/login/pages/LoginPage';
import LogoutPage from '../modules/login/pages/LogoutPage';
import Login from '../modules/login/pages/LoginSso';
import SsoCallback from '../modules/login/pages/SsoCallback';
import Plans from '../pages/Plans';
import ExpiredPage from '../pages/ExpiredPage';
import ApplyTrialPage from '../pages/ApplyTrialPage';
import ApplyAcademicPage from '../pages/ApplyAcademicPage';
import ChatBotPage from '../pages/ChatBotPage';
import BiomarkersPage from '../pages/BiomarkersPage';
import OntologyGraphPage from '../pages/OntologyGraphPage';
import GenePathwaysPage from '../pages/GenePathwaysPage';
import AdvancedSearchPage from '../pages/AdvancedSearchPage';
import UseProtectedRoute from './hooks/useProtectedRoute';
import AdvancedPDFViewer from '../components/PdfViewer/PdfViewer';

// Hooks and reducers
import { useUrlSync } from './hooks/useUrlSync';
import { useMappingContent } from './hooks/useMappingContent';
import { useRouteRedirects } from './hooks/useRouteRedirect';
import { useDataReadyState } from './hooks/useDataReadyState';
import { usePosts } from '../hooks/usePosts';
import { appRouteReducer, initialState } from './reducers/appRouteReducer';
import EnrichmentPage from '../pages/Enrichment';
import PricingTablesAcademic from '@/components/Pricings/PricingTableAcademic';
import PaymentSuccess from '../pages/PaymentSuccess';
import PaymentCancel from '../pages/PaymentCancel';
import ThankYouPage from '../pages/ThankYouPage';
import BookADemo from '../pages/BookADemo';
import AcademicExpirationModal from '@/components/Academic/AcademicExpirationModal';
import Logo from '../components/Common/Logo';
import { TopRightMenu } from '../components/NavMenus/TopNavigation';
import useScrollWindow from '@/hooks/useScrollWindow';
import AcademicLicensePage from '@/components/SignUp/AcademicLicensePage';
import Loader from '@/components/Common/Loader';
import { useMappingStackStore } from '../store/mappingStackStore';
import Scrollytelling from '@/pages/Scrollytelling';
import AidaDisclaimer from '@/components/ChatBot/AidaDisclaimer';
import FeedbackSurveyPage from '../pages/FeedbackSurveyPage';
import { clearPendingSearchRedirect, savePendingSearchRedirect } from '../utils/pendingSearchRedirectStorage';
import Partners from '../pages/Partners';
import Whitepapers from '../pages/Whitepapers';
import WhitepaperRequestPage from '../pages/WhitepaperRequestPage';
import PlansConversionLandingPage from '../pages/PlansConversionLandingPage';
import AcademicUpgradeRibbon from '@/components/Layout/AcademicUpgradeRibbon';
import {
  isAcademicRibbonDismissedToday,
  dismissAcademicRibbonForToday,
} from '@/utils/academicRibbonDismissStorage';
import {
  NAVBAR_HEIGHT_PX,
  ACADEMIC_UPGRADE_RIBBON_HEIGHT_PX,
  LAYOUT_HEADER_OFFSET_CSS_VAR,
} from '@/constants/layoutHeaderInset';

export { NAVBAR_HEIGHT_PX, ACADEMIC_UPGRADE_RIBBON_HEIGHT_PX, LAYOUT_HEADER_OFFSET_CSS_VAR };

const BROCHURE_URL_TEST_DEFAULT =
  'https://brochure-cms-dev.s3.eu-central-1.amazonaws.com/brochure/Brochure.pdf';
const BROCHURE_URL_PROD_DEFAULT =
  'https://brochure-cms-prod.s3.eu-central-1.amazonaws.com/brochure/Brochure.pdf';

const getBrochurePdfUrl = (): string => {
  if (process.env.REACT_APP_BROCHURE_PDF_URL) {
    return process.env.REACT_APP_BROCHURE_PDF_URL;
  }
  const isTest =
    process.env.REACT_APP_URL_BACKEND_API?.includes('test.') ||
    process.env.REACT_APP_URL_API_API?.includes('test.');
  return isTest
    ? process.env.REACT_APP_BROCHURE_URL_TEST || BROCHURE_URL_TEST_DEFAULT
    : process.env.REACT_APP_BROCHURE_URL_PROD || BROCHURE_URL_PROD_DEFAULT;
};

const SearchWithDataReady = ({ dataReady, isLoading }: { dataReady: boolean; isLoading: boolean }) => {
  const showOverlay = isLoading || !dataReady;

  return (
    <div style={{ position: 'relative' }}>
      <Search />
      {showOverlay && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Loader text="Loading..." />
        </div>
      )}
    </div>
  );
};

const RedirectFAQ = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.location.href = 'https://support.disgenet.com/support/home';
  }, [navigate]);

  return null;
};

function AppLayout() {
  const location = useLocation();
  const prevPathnameRef = useRef<string | null>(null);
  const { scrolled, isMobile } = useScrollWindow();
  const isHome = location.pathname === '/';
  const isUnlockLanding = location.pathname === '/unlock-disgenet';
  const { isAuthenticated, isAcademic } = useAuth();
  const showAcademicRibbon = isAuthenticated && isAcademic;
  const [ribbonDismissRev, setRibbonDismissRev] = useState(0);
  const ribbonDismissedToday = useMemo(
    () => isAcademicRibbonDismissedToday(),
    [ribbonDismissRev, location.pathname],
  );
  const showRibbon = showAcademicRibbon && !isUnlockLanding && !ribbonDismissedToday;

  const onRibbonDismiss = useCallback(() => {
    dismissAcademicRibbonForToday();
    setRibbonDismissRev(n => n + 1);
  }, []);

  useEffect(() => {
    const navOnly = isHome ? (showRibbon ? NAVBAR_HEIGHT_PX : 0) : isMobile ? 50 : NAVBAR_HEIGHT_PX;
    const stackPx = navOnly + (showRibbon ? ACADEMIC_UPGRADE_RIBBON_HEIGHT_PX : 0);
    document.documentElement.style.setProperty(LAYOUT_HEADER_OFFSET_CSS_VAR, `${stackPx}px`);
    return () => {
      document.documentElement.style.removeProperty(LAYOUT_HEADER_OFFSET_CSS_VAR);
    };
  }, [isHome, isMobile, showRibbon]);

  useEffect(() => {
    const prev = prevPathnameRef.current;
    const wasOnSearch = prev === '/search' || prev === '/examples';
    const onSearch = location.pathname === '/search' || location.pathname === '/examples';
    if (wasOnSearch && !onSearch) {
      useMappingStackStore.getState().clearFromLevel(0);
    }
    prevPathnameRef.current = location.pathname;
  }, [location.pathname]);

  const paddingTopValue = () => {
    if (isHome) {
      return showRibbon ? NAVBAR_HEIGHT_PX + ACADEMIC_UPGRADE_RIBBON_HEIGHT_PX : 0;
    }
    const base = isMobile ? 50 : NAVBAR_HEIGHT_PX;
    return base + (showRibbon ? ACADEMIC_UPGRADE_RIBBON_HEIGHT_PX : 0);
  };

  const ribbonTopPx = isHome ? NAVBAR_HEIGHT_PX : isMobile ? 50 : NAVBAR_HEIGHT_PX;

  return (
    <>
      {!isHome && (
        <div
          className="header-content"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: scrolled ? '#ffffff' : 'rgba(255, 255, 255, 0.52)',
            WebkitBackdropFilter: 'saturate(180%) blur(12px)',
          }}
        >
          <div>
            <Logo />
          </div>
          <div>
            <TopRightMenu renderizedBy="AppLayout" />
          </div>
        </div>
      )}
      {showRibbon && <AcademicUpgradeRibbon topPx={ribbonTopPx} onDismiss={onRibbonDismiss} />}
      <main
        style={{
          flex: 1,
          paddingTop: paddingTopValue(),
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Outlet />
      </main>
    </>
  );
}

export const CustomRoutes = () => {
  const { posts } = usePosts();
  const [state, dispatch] = useReducer(appRouteReducer, initialState);
  const { isLoading, hasValidParams, isDataReady } = state;
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  // Initialize migration sync
  useContextStoreSync();

  // Get state sources based on migration flags
  const { flags, auth, restrictions, navigation } = useStateSource();

  // Context values with proper typing (fallback for non-migrated components)
  const searchContextValue = searchContext();
  const userAppContextValue = userAppContext();

  // Type guard to ensure contexts are not null
  if (!searchContextValue || !userAppContextValue) {
    return <Loader text="Loading app..." />;
  }

  const { groups } = userAppContextValue;
  const isAcademic = groups?.includes('ROLE_ACADEMIC');

  // Use migrated auth state or fallback to context
  const authState = flags.useAuthStore
    ? {
        authorization: auth.authorization,
        isAuthenticated: auth.isAuthenticated,
        setIsAuthenticated: useAuth().setUser, // This will be handled by the auth store
        setGroups: useAuth().setGroups,
        setAuthError: useAuth().setAuthError,
        setAuthorization: useAuth().setAuthorization,
        restrictSources: restrictions.restrictSources,
      }
    : {
        authorization: userAppContextValue.authorization,
        isAuthenticated: userAppContextValue.isAuthenticated,
        setIsAuthenticated: userAppContextValue.setIsAuthenticated,
        setGroups: userAppContextValue.setGroups,
        setAuthError: userAppContextValue.setAuthError,
        setAuthorization: userAppContextValue.setAuthorization,
        restrictSources: userAppContextValue.restrictSources,
      };

  // Use migrated navigation state or fallback to context
  const navigationState = flags.useNavigationStore
    ? {
        view: navigation.view,
        setView: useNavigation().setView,
        source: navigation.source,
        setSource: useNavigation().setSource,
        tab: navigation.tab,
        setTab: useNavigation().setTab,
        searchItems: navigation.searchItems,
        setSearchItems: useNavigation().setSearchItems,
        setHasValidParams: useNavigation().setHasValidParams,
        mappingContent: navigation.mappingContent,
        setMappingContent: useNavigation().setMappingContent,
      }
    : {
        view: searchContextValue.view,
        setView: searchContextValue.setView,
        source: searchContextValue.source,
        setSource: searchContextValue.setSource,
        tab: searchContextValue.tab,
        setTab: searchContextValue.setTab,
        searchItems: searchContextValue.searchItems,
        setSearchItems: searchContextValue.setSearchItems,
        setHasValidParams: searchContextValue.setHasValidParams,
        mappingContent: searchContextValue.mappingContent,
        setMappingContent: searchContextValue.setMappingContent,
      };

  // Custom hooks for managing different aspects of the component
  useUrlSync({
    searchParams,
    hasValidParams,
    restrictSources: authState.restrictSources,
    isLoading,
    dispatch,
    setView: navigationState.setView,
    setSource: navigationState.setSource,
    setTab: navigationState.setTab,
    setSearchItems: navigationState.setSearchItems,
    setHasValidParams: navigationState.setHasValidParams,
    setMappingContent: navigationState.setMappingContent,
    authorization: authState.authorization,
    isAuthenticated: authState.isAuthenticated,
    setAuthorization: authState.setAuthorization,
    setIsAuthenticated: authState.setIsAuthenticated,
    setGroups: authState.setGroups,
    setAuthError: authState.setAuthError,
    searchItems: navigationState.searchItems,
  });

  useMappingContent({
    searchParams,
    view: navigationState.view,
    tab: navigationState.tab,
    searchItems: navigationState.searchItems,
    setMappingContent: navigationState.setMappingContent,
  });

  useDataReadyState({
    searchItems: navigationState.searchItems,
    mappingContent: navigationState.mappingContent,
    isLoading,
    view: navigationState.view,
    source: navigationState.source,
    tab: navigationState.tab,
    dispatch,
    pathname: location.pathname,
    search: location.search,
  });

  useRouteRedirects({
    posts: posts.map((post: any) => ({ postname: post.link })),
  });

  useEffect(() => {
    if (
      !authState.isAuthenticated &&
      (location.pathname === '/search' || location.pathname === '/examples')
    ) {
      savePendingSearchRedirect(location.pathname, location.search);
    }
    if (!authState.isAuthenticated && location.pathname === '/') {
      clearPendingSearchRedirect();
    }
  }, [authState.isAuthenticated, location.pathname, location.search]);

  useEffect(() => {
    if (!location.pathname.includes['search']) localStorage.setItem('tableColumnOpen', 'false');
  }, [location]);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    if (isAcademic && sessionStorage.getItem('showAcademicUpgradeModal')) {
      setShowUpgradeModal(true);
    }
  }, [isAcademic]);

  const handleCloseUpgradeModal = () => {
    sessionStorage.removeItem('showAcademicUpgradeModal');
    setShowUpgradeModal(false);
  };

  return (
    <>
      <Routes>
        {/* PDF viewer sin navbar */}
        <Route
          path="/brochure"
          element={
            <AdvancedPDFViewer
              pdfUrl={getBrochurePdfUrl()}
              logoUrl="../../../static/images/LogoDisgenet2.png"
              companyName="DISGENET"
            />
          }
        />

        {/* Resto de rutas con navbar global */}
        <Route element={<AppLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/Support" element={<Support />} />
          <Route path="/Privacy-policy" element={<Privacy />} />
          <Route path="/Cookies-policy" element={<CookiesPolicy />} />
          <Route path="/FAQ" element={<RedirectFAQ />} />
          <Route path="/docs" element={<About />} />
          <Route path="/about" element={<Scrollytelling />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/publications/whitepapers/:whitepaperId" element={<WhitepaperRequestPage />} />
          <Route path="/publications/:tabId?" element={<Whitepapers />} />
          <Route path="/Activate" element={<AccountActivation />} />
          <Route path="/ActivationError" element={<AccountActivationError />} />
          <Route path="/Legal" element={<LegalNotices />} />
          <Route path="/legal-notice" element={<LegalNotice />} />
          <Route path="/Profile-area" element={<ProfileArea />} />
          <Route path="/Tools" element={<Api />} />
          <Route path="/Reset" element={<Reset />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/book-a-demo" element={<BookADemo />} />
          <Route
            path="/search"
            element={
              <UseProtectedRoute>
                <SearchWithDataReady dataReady={isDataReady} isLoading={isLoading} />
              </UseProtectedRoute>
            }
          />
          <Route
            path="/examples"
            element={<SearchWithDataReady dataReady={isDataReady} isLoading={isLoading} />}
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/sso" element={<Login />} />
          <Route path="/sso/callback" element={<SsoCallback />} />
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/unlock-disgenet" element={<PlansConversionLandingPage />} />
          <Route path="/desbloquea-disgenet" element={<Navigate to="/unlock-disgenet" replace />} />
          <Route path="/academic-plans" element={<PricingTablesAcademic />} />
          <Route path="/trial-expired" element={<ExpiredPage />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          <Route path="/Error" element={<BadParamsContent />} />
          <Route path="/free-trial-apply" element={<ApplyTrialPage />} />
          <Route path="/academic-apply" element={<ApplyAcademicPage />} />
          <Route
            path="/academic-license"
            element={<AcademicLicensePage onClose={undefined} onAcceptTerms={undefined} />}
          />
          <Route
            path="/Assistant"
            element={
              <UseProtectedRoute>
                <ChatBotPage />
              </UseProtectedRoute>
            }
          />
          <Route path="/AIDA-disclaimer" element={<AidaDisclaimer />} />
          <Route path="/feedback-survey" element={<FeedbackSurveyPage />} />
          <Route path="/biomarkers" element={<BiomarkersPage />} />
          <Route path="/ontology-graph" element={<OntologyGraphPage />} />
          <Route path="/gene-pathways" element={<GenePathwaysPage />} />
          <Route
            path="/advancedSearch"
            element={
              <UseProtectedRoute>
                <AdvancedSearchPage />
              </UseProtectedRoute>
            }
          />
          <Route
            path="/enrichment"
            element={
              <UseProtectedRoute>
                <EnrichmentPage />
              </UseProtectedRoute>
            }
          />
          <Route path="/*" element={<NotFoundContent />} />
        </Route>
      </Routes>
      <AcademicExpirationModal
        open={showUpgradeModal}
        onClose={handleCloseUpgradeModal}
        warningType="upgrade-available"
        expirationStatus={{
          isExpired: false,
          daysUntilExpiration: 0,
          gracePeriodDaysRemaining: 0,
          expirationDate: new Date(),
          canRenew: true,
          hasGracePeriod: false,
        }}
        onRenewal={async () => ({ success: true, message: '' })}
        onCancellation={async () => ({ success: true, message: '' })}
        loading={false}
      />
    </>
  );
};
